import React, { useEffect, memo } from 'react';

const Modal = memo(({ 
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  className = '',
  style = {},
  showCloseButton = true,
  closeOnOverlayClick = true,
  ...props 
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizes = {
    small: { maxWidth: '400px' },
    medium: { maxWidth: '600px' },
    large: { maxWidth: '800px' },
    xlarge: { maxWidth: '1200px' }
  };

  const overlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  };

  const modalStyles = {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    ...sizes[size],
    ...style
  };

  const headerStyles = {
    padding: '20px 24px 16px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const titleStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  };

  const closeButtonStyles = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: '#f3f4f6',
      color: '#374151'
    }
  };

  const contentStyles = {
    padding: '24px',
    overflow: 'auto',
    flex: 1
  };

  return (
    <div 
      className={`modal-overlay ${className}`}
      style={overlayStyles}
      onClick={closeOnOverlayClick ? onClose : undefined}
      {...props}
    >
      <div 
        className="modal-content"
        style={modalStyles}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={headerStyles}>
          {title && <h2 style={titleStyles}>{title}</h2>}
          {showCloseButton && (
            <button
              onClick={onClose}
              style={closeButtonStyles}
              aria-label="Close modal"
            >
              ×
            </button>
          )}
        </div>
        <div style={contentStyles}>
          {children}
        </div>
      </div>
    </div>
  );
});

export default Modal;

Modal.displayName = 'Modal';
